import { OptionsFieldOption } from '@main/dynamic-form';
import { Deps, InferDeps } from '@main/shared/utils';

import { FormFieldCondition, FormFieldConditionParent } from '../lib/conditions';
import { QuestionnaireFormFieldConfig } from '../lib/form-types';
import { mapFieldConditionPartial } from './conditions';
import { mapFieldOptionsPartial } from './fields';

export function updateFieldName(
  field: QuestionnaireFormFieldConfig,
  {
    getFieldName = getRandomUUID,
  }: {
    getFieldName?(field: QuestionnaireFormFieldConfig): string;
  },
): QuestionnaireFormFieldConfig {
  return { ...field, name: getFieldName(field) };
}

export function updateFieldLabel(
  field: QuestionnaireFormFieldConfig,
  {
    getFieldLabel = (f) => (f.label ? `${f.label} (copy)` : f.label),
  }: {
    getFieldLabel?(field: QuestionnaireFormFieldConfig): QuestionnaireFormFieldConfig['label'];
  },
): QuestionnaireFormFieldConfig {
  return { ...field, label: getFieldLabel(field) };
}

export function updateFieldOption(
  option: OptionsFieldOption,
  {
    getOptionName = getRandomUUID,
  }: {
    getOptionName?(option: OptionsFieldOption): string;
  },
): OptionsFieldOption {
  return { ...option, value: getOptionName(option) };
}

export function resetFieldCustomField(
  field: QuestionnaireFormFieldConfig,
): QuestionnaireFormFieldConfig {
  return {
    ...field,
    linkAnswerToVendorCustomField: undefined,
    vendorCustomFieldId: undefined,
  };
}

export function updateFieldSupportingFile(
  field: QuestionnaireFormFieldConfig,
  {
    getFieldUploadId = (f) => f.supportingUploadId,
  }: {
    getFieldUploadId?(field: QuestionnaireFormFieldConfig): string | undefined;
  },
): QuestionnaireFormFieldConfig {
  return { ...field, supportingUploadId: getFieldUploadId(field) };
}

export function updateFieldCondition(
  condition: FormFieldCondition,
  {
    getConditionFieldName = (c) => c.fieldName,
    getConditionValue = (v) => v,
  }: {
    getConditionFieldName?(condition: FormFieldConditionParent): string;
    getConditionValue?(value: string, fieldName: string): string;
  },
): FormFieldCondition {
  if (condition.kind !== 'parent') {
    return condition;
  }

  const parentFieldName = condition.fieldName;

  return {
    ...condition,
    fieldName: getConditionFieldName(condition),
    values: condition.values.map(remapValue),
  };

  function remapValue(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map(remapValue);
    }

    if (typeof value === 'string') {
      return getConditionValue(value, parentFieldName);
    }

    return value;
  }
}

export const duplicateField = Deps.pipe(
  updateFieldName,
  resetFieldCustomField,
  mapFieldOptionsPartial(updateFieldOption),
);

export const remapField = Deps.pipe(
  updateFieldSupportingFile,
  mapFieldConditionPartial(updateFieldCondition),
);

export function cloneFields<T>(
  fields: T,
  {
    uuidMap = new Map(),
    createUuid = getRandomUUID,
    duplicateFields,
    remapFields,
  }: {
    uuidMap?: Map<string, string>;
    createUuid?(prevUuid: string): string;
    duplicateFields(fields: T, deps: InferDeps<typeof duplicateField>): T;
    remapFields(fields: T, deps: InferDeps<typeof remapField>): T;
  },
): T {
  function getUuid(prevUuid: string) {
    const existingUuid = uuidMap.get(prevUuid);
    if (existingUuid !== undefined) {
      return existingUuid;
    }

    const newUuid = createUuid(prevUuid);
    uuidMap.set(prevUuid, newUuid);
    return newUuid;
  }

  const newFields = duplicateFields(fields, {
    getFieldName: (f) => getUuid(f.name),
    getOptionName: (o) => getUuid(o.value),
  });

  return remapFields(newFields, {
    getFieldUploadId: getFieldUploadIdMap(uuidMap),
    getConditionFieldName: (c) => uuidMap.get(c.fieldName) ?? c.fieldName,
    getConditionValue: (v, fieldName) => (uuidMap.has(fieldName) ? (uuidMap.get(v) ?? v) : v),
  });
}

export function getFieldUploadIdMap(uuidMap: Map<string, string>) {
  return (f: QuestionnaireFormFieldConfig) =>
    (f.supportingUploadId && uuidMap.get(f.supportingUploadId)) ?? f.supportingUploadId;
}

function getRandomUUID() {
  return crypto.randomUUID();
}
